import React, { useState, useRef, useEffect, useContext } from 'react';
import { Bot, Menu, X, ChevronDown } from 'lucide-react';
import { COLOR_VARIANTS } from '../ai_config/Role';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { getAllRoles } from '../ai_config/roleUtils';
import CustomPromptManager from '../components/PromptManager';
import BackButton from '../components/BackButton';

const API_URL = 
process.env.NODE_ENV === 'development'
? process.env.REACT_APP_API_URL
: process.env.REACT_APP_SIDEKICK_API_URL;

const allRoles = getAllRoles();  

// Configure DOMPurify to allow <br> tags
const purifyConfig = {
    ADD_TAGS: ['br'],
    ADD_ATTR: []
};

const MobileNav = ({ isOpen, onClose, children }) => (
    <div className={`fixed inset-0 bg-gray-800 bg-opacity-75 z-50 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className={`fixed inset-y-0 left-0 w-64 bg-gray-900 transform transition-transform ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex justify-end p-4">
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );

// AI Model configuration
const AI_MODELS = {
    GEMINI: { name: "Google Gemini", description: "Advanced language model by Google", color: "emerald" },
    CHATGPT: { name: "ChatGPT", description: "OpenAI's language model", color: "teal" },
    LLAMA: { name: "LLaMA", description: "Meta's open source language model", color: "cyan" },
    CLAUDE: { name: "Claude", description: "Anthropic's language model", color: "sky" }
};

const ModelSelector = ({ selectedModel, onModelSelect }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
    // Desktop/Tablet version
    const DesktopSelector = () => (
      <div className="hidden sm:flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
        {Object.entries(AI_MODELS).map(([modelKey, model]) => (
          <button 
            key={modelKey} 
            onClick={() => onModelSelect(modelKey)}
            className={`
              whitespace-nowrap px-3 py-2 rounded-lg transition-colors
              text-sm flex-shrink-0 text-gray-700
              ${selectedModel === modelKey 
                ? `${COLOR_VARIANTS[model.color]} text-white` 
                : 'bg-gray-100 hover:bg-gray-200'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <Bot size={16} />
              <span>{model.name}</span>
            </div>
          </button>
        ))}
      </div>
    );
  
    // Mobile version with dropdown
    const MobileSelector = () => (
      <div className="sm:hidden relative">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full px-3 py-2 bg-gray-100 rounded-lg flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Bot size={16} />
            <span className="text-sm">{AI_MODELS[selectedModel].name}</span>
          </div>
          <ChevronDown 
            size={16}
            className={`transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>
  
        {isDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50">
            {Object.entries(AI_MODELS).map(([modelKey, model]) => (
              <button
                key={modelKey}
                onClick={() => {
                  onModelSelect(modelKey);
                  setIsDropdownOpen(false);
                }}
                className={`
                  w-full px-3 py-2 text-left hover:bg-gray-50
                  ${modelKey === selectedModel ? 'bg-gray-50' : ''}
                  ${modelKey !== Object.keys(AI_MODELS)[Object.keys(AI_MODELS).length - 1] ? 'border-b' : ''}
                `}
              >
                <div className="flex items-center gap-2">
                  <Bot size={16} />
                  <div>
                    <div className="text-sm font-medium">{model.name}</div>
                    <div className="text-xs text-gray-500">{model.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  
    return (
      <div className="px-4 sm:px-8 py-4 border-b">
        <MobileSelector />
        <DesktopSelector />
      </div>
    );
  };
  


const Sidebar = ({ selectedRole, onRoleSelect, isMobile, onClose }) => {
    const sidebarContent = (
      <div className="flex flex-col items-center py-8 space-y-4">
        {Object.entries(allRoles).map(([roleKey, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={roleKey}
              onClick={() => {
                onRoleSelect(roleKey);
                if (isMobile) onClose();
              }}
              className={`p-3 rounded-xl transition-all ${
                selectedRole === roleKey
                  ? `${COLOR_VARIANTS[config.color]} text-white`
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <Icon size={24} />
            </button>
          );
        })}
      </div>
    );
  
    return isMobile ? (
      <MobileNav isOpen={isMobile} onClose={onClose}>
        {sidebarContent}
      </MobileNav>
    ) : (
      <div className="hidden md:block bg-gray-900 w-20 min-h-screen">
        {sidebarContent}
      </div>
    );
  };

const MessageContent = ({ content, isUser, colorVariant }) => {
    const renderContent = () => {
        if (isUser) {
            return content;
        }
        
        let processedContent = content
            .replace(/<\/h[1-6]>/g, '</h$&><br />') 
            .replace(/<\/ul>/g, '</ul><br />') 
            .replace(/<\/p>/g, '</p><br />');
        
        const sanitizedContent = DOMPurify.sanitize(processedContent, purifyConfig);
        return parse(sanitizedContent);
    };

    return (
        <div className={`mb-4 ${isUser ? 'text-right' : 'text-left'}`}>
            <div 
                className={`inline-block max-w-[70%] p-3 rounded-lg ${
                    isUser 
                        ? `${colorVariant} text-white` 
                        : 'bg-gray-100 prose prose-sm max-w-none'
                }`}
            >
                <div className="space-y-2">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};



const MainContent = ({ role, selectedModel, userData }) => {
    const config = allRoles[role];
    const [chatHistories, setChatHistories] = useState({});
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const chatEndRef = useRef(null);
    const currentMessages = chatHistories[role] || [];
    const [prompts, setPrompts] = useState([]);
    const [isAtBottom, setIsAtBottom] = useState(true);

    const handleScroll = () => {
        const { scrollTop, clientHeight, scrollHeight } = chatEndRef.current;
        setIsAtBottom(scrollTop + clientHeight >= scrollHeight);
    };

    useEffect(() => {
        // Scroll only if user is at bottom
        if (isAtBottom) {
            scrollToBottom();
        }
    }, [currentMessages]);

    // Attach scroll event listener
    useEffect(() => {
        const chatContainer = chatEndRef.current;
        chatContainer.addEventListener('scroll', handleScroll);
        
        return () => {
            chatContainer.removeEventListener('scroll', handleScroll);
        };
    }, []);
    
    // Move customPrompts state to use useEffect for updates
    const [customPrompts, setCustomPrompts] = useState(() => {
        try {
            const savedPrompts = localStorage.getItem('customPrompts');
            return savedPrompts ? JSON.parse(savedPrompts) : [];
        } catch (error) {
            console.error('Error loading custom prompts:', error);
            return [];
        }
    });
    
    // Load custom prompts on component mount and when role changes
    useEffect(() => {
        const loadCustomPrompts = () => {
            const savedPrompts = localStorage.getItem('customPrompts') || '[]';
            try {
                const parsedPrompts = JSON.parse(savedPrompts);
                setCustomPrompts(parsedPrompts);
            } catch (error) {
                console.error('Error loading custom prompts:', error);
                setCustomPrompts([]);
            }
        };
        
        loadCustomPrompts();
    }, [role]);

    // Update localStorage whenever customPrompts changes
    useEffect(() => {
        try {
            localStorage.setItem('customPrompts', JSON.stringify(customPrompts));
        } catch (error) {
            console.error('Error saving custom prompts:', error);
        }
    }, [customPrompts]);

    const handleAddCustomPrompt = (newPrompt) => {
        setCustomPrompts(prevPrompts => {
            const promptWithId = {
                ...newPrompt,
                id: Date.now(),
                role: role,
                createdAt: new Date().toISOString() // Optional: add creation timestamp
            };
            const updatedPrompts = [...prevPrompts, promptWithId];
            return updatedPrompts;
        });
    };

    const handleDeleteCustomPrompt = (promptId) => {
        setCustomPrompts(prevPrompts => 
            prevPrompts.filter(prompt => prompt.id !== promptId)
        );
    };

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

     // Function to add a new prompt
    const addPrompt = (newPrompt) => {
        setPrompts(prevPrompts => [...prevPrompts, newPrompt]);
    };

    // Function to delete a prompt
    const deletePrompt = (index) => {
        setPrompts(prevPrompts => prevPrompts.filter((_, i) => i !== index));
    };

    useEffect(() => {
        scrollToBottom();
    }, [currentMessages]);

    const sendMessage = async (messageContent) => {
        // Validate messageContent
        if (!messageContent || typeof messageContent !== 'string' || messageContent.trim() === '') {
            console.error('Invalid messageContent:', messageContent);
            return;
        }
    
        const newMessage = { 
            content: messageContent.trim(), 
            role: 'user', 
            timestamp: new Date().toISOString() 
        };
    
        setChatHistories(prev => ({ ...prev, [role]: [...(prev[role] || []), newMessage] }));
        setInputText('');
        setIsLoading(true);
    
        try {
            const validMessages = [...(chatHistories[role] || []), newMessage]
                .filter(msg => msg.content && typeof msg.content === 'string' && msg.content.trim() !== '')
                .map(msg => ({ role: msg.role, content: msg.content.trim() }));
    
            console.log('Sending messages:', JSON.stringify(validMessages, null, 2));
    
            const response = await fetch(`${API_URL}/ai/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    messages: validMessages,
                    role,
                    model: selectedModel
                })
            });
    
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server error:', errorText);
                throw new Error(`Server error: ${response.status}`);
            }
    
            const data = await response.json();
            if (data.message) {
                const aiResponse = { 
                    content: data.message.content, 
                    role: 'assistant', 
                    timestamp: new Date().toISOString() 
                };
    
                setChatHistories(prev => ({ 
                    ...prev, 
                    [role]: [...(prev[role] || []), aiResponse] 
                }));
            } else {
                throw new Error('Invalid response format from server');
            }
        } catch (error) {
            console.error('Error:', error);
            setChatHistories(prev => ({
                ...prev,
                [role]: [...(prev[role] || []), { 
                    content: `Error: ${error.message}. Please try again.`, 
                    role: 'system', 
                    timestamp: new Date().toISOString() 
                }]
            }));
        } finally {
            setIsLoading(false);
        }
    };

   // Function to generate prompts based on user data
   // Designer
   const generatePrompt = (action) => {
       if (action === 'Create a Logo') {
           if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
               return `You are a professional designer that works for my company called ${userData.business_name}. My company focuses on ${userData.objective}.The competitor in my industry is ${userData.competitors}. My main target is ${userData.target_audience}. Create logo for my company and provide several options`;
           } else {
               console.warn('User data is not defined or missing properties');
               return 'Please provide valid business information.';
           }
       }

        if (action === 'Design Visual for Ads') {
            if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
                return `You are a professional designer that works for my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target is ${userData.target_audience}. Create a visual for ads. The visual dimension requirements are: 1080x1080px; 1080x1920px; 1920x1080px`;
            } else {
                console.warn('User data is not defined or missing properties');
                return 'Please provide valid business information.';
            }
        }

        if (action === 'Create a Banner') {
            if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
                return `You are a professional designer that works for my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target is ${userData.target_audience}. Create a visual for website banner with dimension of: 728x90px; 300x600px; and 300x1050px`;
            } else {
                console.warn('User data is not defined or missing properties');
                return 'Please provide valid business information.';
            }
        }

    // Writer
    if (action === 'Write a Blog Post') {
        if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
            return `You are a professional content writer for my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main audience target is ${userData.target_audience}. Create a blog post that is related with my company. Consider SEO aspect like keyword selection, keyword repetition etc to ensure the article rank well on SERP`;
        } else {
            console.warn('User data is not defined or missing properties');
            return 'Please provide valid business information.';
        }
    }

     if (action === 'Create Social Copy') {
         if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
             return `You are a professional content writer in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now create social media copy with ${userData.brand_tone} tone`;
         } else {
             console.warn('User data is not defined or missing properties');
             return 'Please provide valid business information.';
         }
     }

     if (action === 'Draft Email Campaign') {
         if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
             return `You are a professional content writer in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now create promotional email headline & content promoting my product to new user with ${userData.brand_tone} tone`;
         } else {
             console.warn('User data is not defined or missing properties');
             return 'Please provide valid business information.';
         }
     }

    // PFM
     if (action === 'Create Copy for SEM') {
        if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
            return `You are a professional SEM specialist & in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target is ${userData.target_audience}.Now create 15 headlines (max 30 characters) and 4 descriptions (max 90 characters) for Google search ads that promotes my business with ${userData.brand_tone} tone`;
        } else {
            console.warn('User data is not defined or missing properties');
            return 'Please provide valid business information.';
        }
    }

     if (action === 'Create Copy for Meta Ads') {
         if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
             return `You are a professional Meta ads specialist & in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target is ${userData.target_audience}.Now create 5 primary texts (max 180 characters) and 4 headlines (max 40 characters) for Meta ads that promotes my business with ${userData.brand_tone} tone`;
         } else {
             console.warn('User data is not defined or missing properties');
             return 'Please provide valid business information.';
         }
     }

     if (action === 'Create Paid Campaign Structure') {
         if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
             return `You are a professional paid ads specialist & in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target is ${userData.target_audience}.Now create paid campaign structure for Google Ads, Meta Ads, and TikTok ads that align with my business objective`;
         } else {
             console.warn('User data is not defined or missing properties');
             return 'Please provide valid business information.';
         }
     }

     if (action === 'Generate Visual Headline and Subheadline for Paid Ads') {
        if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
            return `You are a professional paid ads specialist & in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target is ${userData.target_audience}.Now create several options for visual headline and subheadline to promote my business. The headline needs to be thumbstopping. Provide several options with different hooks, e.g. open headline with question, highlight recent trends, etc`
        } else {
            console.warn('User data is not defined or missing properties');
            return 'Please provide valid business information.';
        }
    }

    // Data Analyst
    if (action === 'Analyze Trends') {
       if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
           return `You are a professional data analyst in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now analyze the business trend in my sector, provide usefull insight and recommendation to improve my business growth`;
       } else {
           console.warn('User data is not defined or missing properties');
           return 'Please provide valid business information.';
       }
   }

    if (action === 'Create Reporting Format') {
        if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
            return `You are a professional data analyst in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now create a reporting format to monitor my day-to-day business operation. Provide daily, weekly, and monthly reporting format`;
        } else {
            console.warn('User data is not defined or missing properties');
            return 'Please provide valid business information.';
        }
    }

    // Virtual Assistant
    if (action === 'Research Topics') {
       if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
           return `You are a professional virtual assistant in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now provide an in-depth research about my business and provide usefull insight`;
       } else {
           console.warn('User data is not defined or missing properties');
           return 'Please provide valid business information.';
       }
   }

    if (action === 'Summarize Content') {
        if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
            return `You are a professional content writer in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now create social media copy with ${userData.brand_tone} tone`;
        } else {
            console.warn('User data is not defined or missing properties');
            return 'Please provide valid business information.';
        }
    }

    // Business Analyst
    if (action === 'Strategic Planning') {
        if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
            return `You are a professional business analyst in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now do strategic planning and breakdown all action plan to grow my business this year`;
        } else {
            console.warn('User data is not defined or missing properties');
            return 'Please provide valid business information.';
        }
    }
 
     if (action === 'Market Analysis') {
         if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
             return `You are a professional business analyst in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now do market analysis and provide suggestion to be competitive in current market landscape`;
         } else {
             console.warn('User data is not defined or missing properties');
             return 'Please provide valid business information.';
         }
     }

     if (action === 'Risk Assessment') {
        if (userData && userData.business_name && userData.objective && userData.industry && userData.target_audience && userData.objective && userData.objective) {
            return `You are a professional business analyst in my company called ${userData.business_name}. My company focuses on ${userData.objective}. The competitor in my industry is ${userData.competitors}. My main target audience is ${userData.target_audience}.Now do risk assesment for my business and provide usefull insight related to risk management`;
        } else {
            console.warn('User data is not defined or missing properties');
            return 'Please provide valid business information.';
        }
    }

       return action; // Fallback to action name if no specific prompt is generated

   };

   
   const handleActionClick = async (action) => {
       if (isLoading) return;
       const prompt = generatePrompt(action);
       await sendMessage(prompt); // Use generated prompt instead of action name
   };

   return (
    <div className="flex flex-col h-screen">
      <header className="bg-white py-4 px-4 sm:px-8 border-b flex-shrink-0">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <config.icon className={`text-${config.color}-500`} size={20} />
              {config.title}
            </h1>
            <p className="text-gray-600 text-sm mt-1">{config.subtitle}</p>
            <p className="text-gray-500 text-xs mt-1">
              Using {AI_MODELS[selectedModel].name}
            </p>
          </div>
          <BackButton />
        </div>
      </header>
        
        <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-y-auto text-gray-700 bg-white mx-8 my-6 p-4 rounded-lg">
                <div className="space-y-4">
                    {currentMessages.map((message, index) => (
                        <MessageContent 
                            key={index}
                            content={message.content}
                            isUser={message.role === 'user'}
                            colorVariant={COLOR_VARIANTS[config.color]}
                        />
                    ))}
                    {isLoading && (
                        <div className="text-left">
                            <div className="inline-block bg-gray-100 p-3 rounded-lg">
                                Thinking...
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>
            </main>
            <footer className="bg-customColor1 p-4">
                <div className="mx-8 flex flex-wrap gap-4 mb-4">
                    {config.actions.map((action) => (
                        <button 
                            key={action} 
                            onClick={() => handleActionClick(action)} 
                            disabled={isLoading}
                            className={`${COLOR_VARIANTS[config.color]} text-white px-6 py-2 rounded-full transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {action}
                        </button>
                    ))}
                    {customPrompts
                            .filter(prompt => prompt.role === role)
                            .map((prompt) => (
                                <div key={prompt.id} className="relative inline-block">
                                    <button 
                                        onClick={() => handleActionClick(prompt.content)}
                                        disabled={isLoading}
                                        className={`hover:bg-gray-700 bg-gray-600 text-white px-6 py-2 rounded-full transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {prompt.name}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteCustomPrompt(prompt.id)}
                                        className="ml-1 text-gray-400 hover:text-gray-600"
                                    >
                                        ×
                                    </button>
                                </div>
                        ))}
                        
                        <CustomPromptManager 
                            role={role}
                            onAddCustomPrompt={handleAddCustomPrompt}
                        />
                    </div>
                <form 
                    onSubmit={(e) => { e.preventDefault(); sendMessage(inputText); }} 
                    className="mx-8 relative"
                    data-testid="chat-form"
                >
                    <input 
                        type="text" 
                        value={inputText} 
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder={config.defaultPrompt} 
                        className="w-full p-3 rounded-lg border border-gray-300 pr-12 text-gray-700" 
                        disabled={isLoading} 
                    />
                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-${config.color}-500 hover:text-${config.color}-700 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        aria-label="Submit prompt"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                    </button>
                </form>
            </footer>
        </div>
    </div>
);
};


const ChatPage = () => {  
    
const [selectedRole, setSelectedRole] = useState('DESIGNER');
const [selectedModel, setSelectedModel] = useState('GEMINI');
const [userData, setUserData] = useState(null);
const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

useEffect(() => {
    const fetchUserData = async () => {
        try {
            const loggedInUserId = parseInt(localStorage.getItem('userId'));
            const response = await fetch(`${API_URL}/users/${loggedInUserId}`);
            if (!response.ok) throw new Error('Failed to fetch user data');
            const data = await response.json();
            setUserData(data);
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };
    fetchUserData();
}, []);

return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile menu button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700"
        onClick={() => setIsMobileNavOpen(true)}
      >
        <Menu size={24} />
      </button>

      <Sidebar
        selectedRole={selectedRole}
        onRoleSelect={setSelectedRole}
        isMobile={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
      />

      <div className="flex-1 flex flex-col">
        <ModelSelector selectedModel={selectedModel} onModelSelect={setSelectedModel} />
        {userData ? (
          <MainContent role={selectedRole} selectedModel={selectedModel} userData={userData} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-gray-500">Loading user data...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;